import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import CircuitForm from '../../components/forms/CircuitForm';
import CircuitoViewModal from '../../components/modals/CircuitoViewModal';
import circuitService, { Circuito, FtthCaixa, CircuitFilters } from '../../services/circuitService';
import { ftthCaixaService } from '../../services/ftthCaixaService';

interface CircuitosTabProps {
  createTrigger?: number;
}

const CircuitosTab: React.FC<CircuitosTabProps> = ({ createTrigger = 0 }) => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const [circuitos, setCircuitos] = useState<Circuito[]>([]);
  const [caixas, setCaixas] = useState<FtthCaixa[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginação
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Filtros
  const [filters, setFilters] = useState<CircuitFilters>({
    limit: 10,
    search: '',
    caixa_id: undefined,
    situacao: undefined
  });

  // Modal
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCircuit, setSelectedCircuit] = useState<Circuito | null>(null);

  // Modal de visualização
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCircuitId, setSelectedCircuitId] = useState<number | null>(null);

  // Carregar dados iniciais
  useEffect(() => {
    loadCaixas();
    loadCircuitos();
  }, []);

  // Recarregar quando filtros ou página mudarem
  useEffect(() => {
    loadCircuitos();
  }, [filters, page]);

  const loadCircuitos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await circuitService.getCircuitos({
        ...filters,
        page
      });

      console.log('🔧 Resposta do servidor no circuitosmanagement:', response);

      setCircuitos(response.circuitos);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      console.error('Erro ao carregar circuitos:', error);
      setError('Erro ao carregar circuitos');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  const loadCaixas = async () => {
    try {
      const data = await ftthCaixaService.getCaixas();
      setCaixas(data.caixas);
      console.log('🔍 CircuitosManagement  - response:', data);
    } catch (error) {
      console.error('Erro ao carregar Caixas:', error);
    }
  };

  const handleFilterChange = (field: keyof CircuitFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
    setPage(1); // Reset para primeira página
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('search', event.target.value);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleCreate = () => {
    setSelectedCircuit(null);
    setFormOpen(true);
  };

  useEffect(() => {
    if (createTrigger === 0) return;
    handleCreate();
  }, [createTrigger]);

  const handleEdit = (circuit: Circuito) => {
    setSelectedCircuit(circuit);
    setFormOpen(true);
  };

  const handleDelete = async (circuit: Circuito) => {
    if (window.confirm(`Tem certeza que deseja remover o circuito ${circuit.identificacao}?`)) {
      try {
        await circuitService.deleteCircuit(circuit.id);
        loadCircuitos();
      } catch (error) {
        console.error('Erro ao remover circuito:', error);
        alert('Erro ao remover circuito');
      }
    }
  };

  const handleFormSuccess = () => {
    loadCircuitos();
  };

  const handleView = (circuito: Circuito) => {
    setSelectedCircuitId(circuito.id);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedCircuitId(null);
  };

  const clearFilters = () => {
    setFilters({
      limit: 10,
      search: '',
      caixa_id: undefined,
      situacao: undefined
    });
    setPage(1);
  };

  const getSituacaoColor = (situacao: string) => {
    switch (situacao) {
      case 'livre':
        return 'success';
      case 'ocupado':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSituacaoLabel = (situacao: string) => {
    switch (situacao) {
      case 'livre':
        return 'Livre';
      case 'ocupado':
        return 'Ocupado';
      default:
        return situacao;
    }
  };

  // Estatísticas rápidas
  const livres = circuitos.filter(c => c.situacao === 'livre').length;
  const ocupados = circuitos.filter(c => c.situacao === 'ocupado').length;

  return (
    <Box sx={{ p: 0 }}>
      {/* Estatísticas rápidas */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                Total de Circuitos
              </Typography>
              <Typography variant="h4" sx={{ fontSize: '1.5rem', lineHeight: 1.2 }}>
                {total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                Circuitos Livres
              </Typography>
              <Typography variant="h4" color="success.main" sx={{ fontSize: '1.5rem', lineHeight: 1.2 }}>
                {livres}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                Circuitos Ocupados
              </Typography>
              <Typography variant="h4" color="error.main" sx={{ fontSize: '1.5rem', lineHeight: 1.2 }}>
                {ocupados}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
          <FilterIcon sx={{ mr: 1 }} />
          Filtros
        </Typography>

        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Buscar"
              placeholder="Identificação ou endereço..."
              value={filters.search || ''}
              onChange={handleSearch}
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>CAIXA</InputLabel>
              <Select
                value={filters.caixa_id || ''}
                onChange={(e) => handleFilterChange('caixa_id', e.target.value)}
                label="CAIXA"
              >
                <MenuItem value="">Todas</MenuItem>
                {caixas.map((caixa) => (
                  <MenuItem key={caixa.id} value={caixa.id}>
                    {caixa.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Situação</InputLabel>
              <Select
                value={filters.situacao || ''}
                onChange={(e) => handleFilterChange('situacao', e.target.value)}
                label="Situação"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="livre">Livre</MenuItem>
                <MenuItem value="ocupado">Ocupado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Atualizar">
                <IconButton onClick={loadCircuitos} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" onClick={clearFilters} size="small" sx={{ fontSize: '0.8rem' }}>
                Limpar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabela */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, bgcolor: alpha(primario, 0.04), borderBottom: `1px solid ${alpha(primario, 0.1)}` }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Lista de Circuitos
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
            <TableHead>
              <TableRow sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                  fontWeight: 700,
                  fontSize: '0.73rem',
                  py: 0.8,
                  borderBottom: `2px solid ${alpha(primario, 0.2)}`,
                },
              }}>
                <TableCell>Identificação</TableCell>
                <TableCell>CAIXA</TableCell>
                <TableCell>Endereço</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : circuitos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum circuito encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                circuitos.map((circuit) => (
                  <TableRow key={circuit.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        {circuit.identificacao}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {circuit.caixa?.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {circuit.endereco}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getSituacaoLabel(circuit.situacao)}
                        color={getSituacaoColor(circuit.situacao) as any}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      {circuit.contrato ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem', mb: 0.1 }}>
                            {circuit.contrato.cliente?.nome || 'Cliente não informado'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {circuit.contrato.titulo}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Sem contrato
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          onClick={() => handleView(circuit)}
                          disabled={loading}
                          color="info"
                          sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(circuit)}
                          disabled={loading}
                          sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(circuit)}
                          disabled={loading}
                          color="error"
                          sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.5,
              px: 2,
              bgcolor: alpha(primario, 0.04),
              borderTop: `2px solid ${alpha(primario, 0.2)}`,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              disabled={loading}
            />
          </Box>
        )}
      </Paper>

      {/* Modal do formulário */}
      <CircuitForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
        circuito={selectedCircuit}
      />

      {/* Modal de visualização */}
      <CircuitoViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        circuitoId={selectedCircuitId ?? 0}
      />
    </Box>
  );
};

export default CircuitosTab; 